import { COUNTRIES, PLAYER_POSITIONS } from '../../../helpers/constants'
import { ERROR, READY, useVisualState } from '../../../hooks/useVisualState'
import { Form, FormBand } from '../../../components/Form/Form'
import RadioButton, { RadioButtonGroup } from '../../../components/RadioButton/RadioButton'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { calculateAge, monthNames } from '../../../helpers/date'
import { oldGet, oldPost } from '../../../helpers/requestOld'

import Button from '../../../components/Button/Button'
import FileDropBox from '../../../components/FileDropBox/FileDropBox'
import FileUploader from '../../../components/FileUploader/FileUploader'
import GenericErrorModal from '../../../modules/GenericErrorModal/GenericErrorModal'
import Modal from '../../../components/Modal/Modal'
import ModalContent from '../../../components/Modal/ModalContent'
import ModalFooter from '../../../components/Modal/ModalFooter'
import ModalHeader from '../../../components/Modal/ModalHeader'
import PhotoUploader from '../../../components/PhotoUploader/PhotoUploader'
import ProgressBar from '../../../components/ProgressBar/ProgressBar'
import Select from '../../../components/Select/Select'
import SelectItem from '../../../components/Select/SelectItem'
import TagsInput from '../../../components/TagsInput/TagsInput'
import TextField from '../../../components/TextField/TextField'
import { closeEvent } from '../../../components/Modal/ModalEvents'
import t from '../../../helpers/i18n'
import { uploadFileHelper } from '../../../helpers/upload'
import useForceUpdate from '../../../hooks/useForceUpdate'
import useObjectState from '../../../hooks/useObjectState'
import { useParams } from 'react-router-dom'

const emailValidation = (text) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(text) ? false : t('validations.invalid_email')
const phoneValidation = (text) => /^.{1,20}$/.test(text) ? false : t('validations.phone_number_is_invalid')

const filterOutputPositions = (text) => {
  const item = PLAYER_POSITIONS.find(row => row.value === text)
  return item ? item.code : false
}

const filterInputPositions = (values) => {
  if (!values) return ''
  return values.map(value => value.value).join(',')
}

const OldPlayerFormModal = ({ onCloseRequested, onNewPlayer }) => {
  const [formValid, setFormValid] = useState(false)
  const [uploading, setUploading] = useObjectState()
  const [error, setError] = useState()
  const createPlayerData = useRef()
  const createPlayerPending = useRef()
  const formModal = useRef()
  const uploadModal = useRef()
  const cancel = useRef(false)
  const [[playerState, player], setPlayer] = useVisualState()
  const { teamId, playerId: aPlayerId } = useParams()
  const playerId = aPlayerId ? aPlayerId : 'new'
  const [, forceUpdate] = useForceUpdate()
  const currentYear = new Date().getFullYear()
  const birthDate = (createPlayerData.current && createPlayerData.current.birth_year && createPlayerData.current.birth_month && createPlayerData.current.birth_day) ?
                    new Date(createPlayerData.current.birth_year, createPlayerData.current.birth_month, createPlayerData.current.birth_day) : false
  const age = birthDate ? calculateAge(birthDate) : false
  const isNew = playerId === 'new'

  const handleSavePlayer = useCallback(() => {
    cancel.current = false
    uploadFileHelper(createPlayerPending.current, { avatar: 'avatar', parental_control_file: 'files', archive: 'files' }, setUploading)
      .then(data => {
        if (!cancel.current) {
          oldPost('/delegate/teams/' + teamId + '/players/' + playerId, { ...createPlayerData.current, ...data })
            .then(result => {
              if (onNewPlayer) onNewPlayer(result.playerId)
              closeEvent(formModal.current, false)
            })
            .catch(error => setError(error))
        }
      })

  }, [teamId, playerId, setUploading, onNewPlayer])

  const handleCancelUpload = useCallback(() => {
    cancel.current = true
    closeEvent(uploadModal.current, false)
  }, [])

  const handleCloseError = useCallback(() => {
    setError(false)
    setUploading(false)
  }, [])

  useEffect(() => {
    oldGet('/delegate/teams/' + teamId + '/players/' + playerId)
      .then(response => setPlayer(READY, response))
      .catch(error => setPlayer(ERROR, error))
  }, [setPlayer, teamId, playerId, isNew])

  return (
    <div>
      {playerState === READY &&
        <Modal onCloseRequested={onCloseRequested} elementRef={formModal}>
          <ModalHeader title={isNew ? t('modal_headers.Add_new_player') : t('modal_headers.Edit_player')} />
          <Form data={createPlayerData} pending={createPlayerPending} onValidityChange={setFormValid} onChange={forceUpdate}>
            <ModalContent>
              <FormBand text={t("titles.Personal_Information")} />
              <div className="-layout-21">
                <div>
                  <TextField
                    name="first_name"
                    label={t('labels.First_Name')}
                    mode="entity-name"
                    maxLength={100}
                    minLength={2}
                    required
                    initialValue={player.first_name}
                  />
                  <TextField
                    name="last_name"
                    label={t('labels.Last_Name')}
                    mode="entity-name"
                    maxLength={100}
                    minLength={2}
                    required
                    initialValue={player.last_name}
                  />
                </div>
                <div>
                  <PhotoUploader
                    name="avatar"
                    label={t('labels.Pic')}
                    aspect={1}
                    source={player.avatar}
                    circular
                  />
                </div>
              </div>
              <div className="-layout-111 -small">
                <Select
                  name="birth_day"
                  label={t("labels.Day")}
                  initialValue={player.birth_day}
                  required
                >
                  <SelectItem/>
                  {Array.from({ length: 31}).map((_, index) => (
                    <SelectItem key={index} value={index + 1}>{index + 1}</SelectItem>
                  ))}
                </Select>
                <Select
                  name="birth_month"
                  label={t("labels.Month")}
                  initialValue={player.birth_month}
                  required
                >
                  <SelectItem/>
                  {Array.from({ length: 12}).map((_, index) => (
                    <SelectItem key={index} value={index + 1}>{monthNames[index]}</SelectItem>
                  ))}
                </Select>
                <Select
                  name="birth_year"
                  label={t("labels.Year")}
                  initialValue={player.birth_year}
                  required
                >
                  <SelectItem/>
                  {Array.from({ length: 100}).map((_, index) => (
                    <SelectItem key={index} value={currentYear - index}>{currentYear - index}</SelectItem>
                  ))}
                </Select>
              </div>
                <Select
                  name="age_status"
                  label={t("Age Status")}
                  initialValue={player.age_status}
                >
                  <SelectItem value="">-</SelectItem>
                  <SelectItem value="MINOR">{t('select_item.Minor')}</SelectItem>
                  <SelectItem value="MEDIUM">{t('select_item.Medium')}</SelectItem>
                  <SelectItem value="MAYOR">{(t('select_item.Mayor'))}</SelectItem>
              </Select>
              <RadioButtonGroup
                name="gender"
                label={t("labels.Gender")}
                orientation="horizontal"
                required
              >
                <RadioButton value="MALE" label={t("radio_buttons.Male")} initialValue={player.gender} />
                <RadioButton value="FEMALE" label={t("radio_buttons.Female")} initialValue={player.gender} />
              </RadioButtonGroup>
            </ModalContent>
            {age && age < 18 &&
              <ModalContent>
                <FormBand text={t("titles.Parental_Control")} />
                <FileUploader
                  name="parental_control_file"
                  label={t('labels.player_is_underage_You_must_attach_an_official_document')}
                  filename={player.parental_control_file_name}
                  source={player.parental_control_file_id && (process.env.REACT_APP_IMAGE_BASE_URL + '/files/' + player.parental_control_file_id + '?download')}
                  />
              </ModalContent>
            }
            <ModalContent>
              <FormBand text={t("titles.Optional_Information")} />
              <Select
                  name="nationality"
                  label={t("labels.Nationality")}
                  initialValue={player.nationality}
              >
                <SelectItem></SelectItem>
                {COUNTRIES.map(country =>
                  <SelectItem key={country} value={country}>{t(country)}</SelectItem>
                )}
              </Select>
              <TextField
                name="shirt_number"
                label={t('labels.Jersey_number')}
                initialValue={player.shirt_number}
                />
              <TagsInput
                name="positions"
                label={t('labels.Positions')}
                initialValue={PLAYER_POSITIONS.filter(row => player.positions && player.positions.includes(row.code))}
                filterOutput={filterOutputPositions}
                filterInput={filterInputPositions}
                options={PLAYER_POSITIONS}
                limit={3}
                />
              <FileDropBox
                name="archive"
                label={t('Player_Archive')}
                files={player.archive}
              />
              {player.player_dynamic_fields && player.player_dynamic_fields.map(field =>
                <TextField
                  key={"field_" + field}
                  name={"field_" + field}
                  label={t("dynamic_fields." + field)}
                  initialValue={player.dynamic_fields[field] || ''}
                />
              )}
            </ModalContent>
            <ModalContent>
              <FormBand text={t("titles.Contact")} />
              <TextField
                name="email"
                label={t('labels.Email')}
                onValidation={emailValidation}
                initialValue={player.email}
                />
              <TextField
                name="phone"
                label={t('labels.Contact_phone')}
                onValidation={phoneValidation}
                maxLength={50}
                initialValue={player.phone}
                />
            </ModalContent>
          </Form>
          <ModalFooter>
            <div>
              <Button onClick={event => closeEvent(event.currentTarget, false)} label={t("buttons.Cancel")} />
            </div>
            <div>
              <Button disabled={!formValid} onClick={handleSavePlayer} label={t("buttons.Save")} />
            </div>
          </ModalFooter>
          {uploading && !cancel.current &&
            <Modal elementRef={uploadModal} onCloseRequested={handleCancelUpload}>
              <ModalHeader title={t("modal_headers.Uploading_files")} back={false} />
              <ModalContent dialog>
                {Object.keys(uploading).map(key =>
                  <div key={key}>
                    <div className="text-label color-text">{FILE_NAMES[key] || createPlayerPending.current[key].value.name}</div>
                    <ProgressBar percent={uploading[key]} />
                  </div>
                )}
              </ModalContent>
            </Modal>
          }
          {error && <GenericErrorModal error={error} i18nPrefix="OldPlayerFormModal" onCloseRequested={handleCloseError} />}
        </Modal>
      }
    </div>
  )
}

const FILE_NAMES = {
  "avatar": t("Foto del jugador"),
  "parental_control_file": t("Control Parental"),
  "archive": t("Archivos del jugador")
}

export default OldPlayerFormModal